import { zodResolver } from '@hookform/resolvers/zod';
import type { ReactNode } from 'react';
import type { Control } from 'react-hook-form';
import { useForm, useWatch } from 'react-hook-form';

import { DateRangePicker } from '@/components/common/date-picker';
import { Select } from '@/components/common/select';
import { Drawer } from '@/components/feedback/drawer';
import { FormField, NumberField } from '@/components/forms';
import { appTzLabel, fromDateRange, toDayjsRange } from '@/lib/date';

import { toDefaults, toFilters } from '../filters';
import { PAYMENT_FOR_LABEL } from '../format';
import { paymentFiltersSchema } from '../schemas';
import type {
  PaymentFilterFormValues,
  PaymentFor,
  PaymentListFilters,
  PaymentStatusFilter,
} from '../types';

import { PaymentStatusChip } from './payment-status-chip';

const PAYMENT_FOR_OPTIONS = (Object.keys(PAYMENT_FOR_LABEL) as PaymentFor[]).map((value) => ({
  value,
  label: PAYMENT_FOR_LABEL[value],
}));

/** The 9 filter statuses (no `capture_failed` — it is matched by the `failed` option). */
const STATUS_OPTIONS = (
  [
    'pending',
    'success',
    'failed',
    'on_hold',
    'debited',
    'released',
    'refund_initiated',
    'refunded',
    'disputed',
  ] as PaymentStatusFilter[]
).map((value) => ({ value, label: <PaymentStatusChip status={value} /> }));

const METHOD_OPTIONS = [
  { value: 'card', label: 'Card' },
  { value: 'net_banking', label: 'Net Banking' },
];

type FilterSelectName = 'paymentFor' | 'paymentStatus' | 'paymentMethod';

type FilterSelectProps = {
  name: FilterSelectName;
  control: Control<PaymentFilterFormValues>;
  label: string;
  options: { value: string; label: ReactNode }[];
};

/**
 * A single-select filter field that clears to `null` rather than `undefined`.
 *
 * antd's clear button fires `onChange(undefined)`, and RHF treats an `undefined` value as
 * "reset to the field's `defaultValue`" — so on a re-opened drawer (seeded from the last applied
 * filter) clearing would snap the select back to its seeded value instead of emptying it.
 * Normalising the cleared value to `null` (a *defined* value RHF keeps as-is) clears it; the schema
 * accepts `null` (`.nullish()`) and `toFilters` folds it back to "no filter". We wrap `FormField` +
 * `Select` directly because the shared `<SelectField>` hardcodes `onChange={field.onChange}`.
 */
function FilterSelect({ name, control, label, options }: FilterSelectProps) {
  return (
    <FormField name={name} control={control} label={label}>
      {({ field, fieldState }) => (
        <Select
          id={name}
          style={{ width: '100%' }}
          placeholder="Select"
          allowClear
          options={options}
          status={fieldState.error ? 'error' : undefined}
          value={field.value ?? undefined}
          onChange={(next) => field.onChange(next ?? null)}
          onBlur={field.onBlur}
          ref={field.ref}
        />
      )}
    </FormField>
  );
}

export type PaymentFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: PaymentListFilters;
  onApply: (filters: PaymentListFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<PaymentFilterDrawerProps, 'open'>;

function FilterBody({ value, onApply, onClose }: BodyProps) {
  const draft = toDefaults(value);
  const form = useForm<PaymentFilterFormValues>({
    resolver: zodResolver(paymentFiltersSchema),
    // `toDefaults` yields `undefined` for the string date fields, but the schema requires
    // `nullable`; coerce to null so an untouched form validates (mirrors OrderFilterDrawer).
    defaultValues: { ...draft, paidFrom: draft.paidFrom ?? null, paidTo: draft.paidTo ?? null },
    mode: 'onBlur',
  });

  const paidFrom = useWatch({ control: form.control, name: 'paidFrom' });
  const paidTo = useWatch({ control: form.control, name: 'paidTo' });
  // Single date-range picker per design; maps to the paidFrom/paidTo wire params.
  const dateRange = paidFrom && paidTo ? toDayjsRange([paidFrom, paidTo]) : null;

  const handleApply = form.handleSubmit((values) => {
    onApply(toFilters(values));
    onClose();
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      width={400}
      primaryText="Apply"
      onPrimary={handleApply}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <FilterSelect
        name="paymentFor"
        control={form.control}
        label="Payment For"
        options={PAYMENT_FOR_OPTIONS}
      />

      <FilterSelect
        name="paymentStatus"
        control={form.control}
        label="Payment Status"
        options={STATUS_OPTIONS}
      />

      <FilterSelect
        name="paymentMethod"
        control={form.control}
        label="Payment Method"
        options={METHOD_OPTIONS}
      />

      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend>Number of Devices</legend>
        <div style={{ display: 'flex', gap: 12 }}>
          <NumberField
            name="deviceCountFrom"
            control={form.control}
            aria-label="Number of Devices From"
            placeholder="From"
            min={0}
            precision={0}
            style={{ width: '100%' }}
          />
          <NumberField
            name="deviceCountTo"
            control={form.control}
            aria-label="Number of Devices To"
            placeholder="To"
            min={0}
            precision={0}
            style={{ width: '100%' }}
          />
        </div>
      </fieldset>

      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend>Price Range</legend>
        <div style={{ display: 'flex', gap: 12 }}>
          <NumberField
            name="amountFrom"
            control={form.control}
            aria-label="Price From"
            placeholder="From"
            min={0}
            precision={2}
            style={{ width: '100%' }}
          />
          <NumberField
            name="amountTo"
            control={form.control}
            aria-label="Price To"
            placeholder="To"
            min={0}
            precision={2}
            style={{ width: '100%' }}
          />
        </div>
      </fieldset>

      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend>{appTzLabel('Payment Date Range')}</legend>
        <DateRangePicker
          aria-label={appTzLabel('Payment Date Range')}
          format="MM/DD/YYYY"
          value={dateRange}
          onChange={(range) => {
            const iso = fromDateRange(range);
            form.setValue('paidFrom', iso?.[0] ?? null, { shouldValidate: true });
            form.setValue('paidTo', iso?.[1] ?? null, { shouldValidate: true });
          }}
          style={{ width: '100%' }}
        />
      </fieldset>
    </Drawer>
  );
}

export function PaymentFilterDrawer({ open, ...rest }: PaymentFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close; remounts fresh on open so the draft re-seeds from the last applied `value`.
  return <FilterBody {...rest} />;
}
